import type { HTMLAttributes, HTMLInputTypeAttribute } from "react";
import type { FieldError } from "react-hook-form";
import type {
  ProfileField,
  ProfileFieldOption,
  ProfileFieldType,
  ProfileFieldValue,
} from "../../types";
import {
  fieldAllowsOtherValue,
  isCustomOtherValue,
  isOtherOptionValue,
  normalizeProfileFieldValue,
} from "../../utils/profileFields";

const OTHER_OPTION: ProfileFieldOption = {
  label: "Other",
  value: "other",
};

type DynamicFieldControlProps = {
  className?: string;
  error?: FieldError | string;
  field: ProfileField;
  idPrefix?: string;
  onChange: (value: ProfileFieldValue) => void;
  requiredIndicator?: boolean;
  value: ProfileFieldValue | undefined;
  hideLabel?: boolean;
};

export function DynamicFieldControl({
  className,
  error,
  field,
  idPrefix = "pf",
  onChange,
  requiredIndicator = false,
  value,
  hideLabel = false,
}: DynamicFieldControlProps) {
  const currentValue = normalizeProfileFieldValue(field, value);

  if (field.type === "multiSelect" || field.type === "checkbox") {
    return (
      <MultiSelectField
        className={className}
        error={error}
        hideLabel={hideLabel}
        field={field}
        idPrefix={idPrefix}
        onChange={onChange}
        requiredIndicator={requiredIndicator}
        value={Array.isArray(currentValue) ? currentValue : []}
      />
    );
  }

  if (field.type === "select") {
    return (
      <SelectField
        className={className}
        error={error}
        field={field}
        hideLabel={hideLabel}
        idPrefix={idPrefix}
        onChange={onChange}
        requiredIndicator={requiredIndicator}
        value={typeof currentValue === "string" ? currentValue : ""}
      />
    );
  }

  if (field.type === "radio") {
    return (
      <RadioField
        className={className}
        error={error}
        field={field}
        idPrefix={idPrefix}
        hideLabel={hideLabel}
        onChange={onChange}
        requiredIndicator={requiredIndicator}
        value={typeof currentValue === "string" ? currentValue : ""}
      />
    );
  }

  return (
    <TextLikeField
      className={className}
      error={error}
      field={field}
      idPrefix={idPrefix}
      hideLabel={hideLabel}
      onChange={onChange}
      requiredIndicator={requiredIndicator}
      value={typeof currentValue === "string" ? currentValue : ""}
    />
  );
}

type SharedFieldProps = {
  className?: string;
  error?: FieldError | string;
  field: ProfileField;
  idPrefix: string;
  onChange: (value: ProfileFieldValue) => void;
  requiredIndicator: boolean;
  hideLabel?: boolean;
};

function SelectField({
  className,
  error,
  field,
  idPrefix,
  onChange,
  requiredIndicator,
  value,
  hideLabel,
}: SharedFieldProps & { value: string }) {
  const fieldId = `${idPrefix}-${field.fieldKey}`;
  const errorId = `${fieldId}-error`;
  const supportsOther = fieldAllowsOtherValue(field);
  const options = supportsOther
    ? [...field.options, OTHER_OPTION]
    : field.options;
  const otherText =
    supportsOther && isCustomOtherValue(field, value) ? value : "";
  const selectedValue = supportsOther && otherText ? "other" : value;

  return (
    <div className={fieldClassName(className)}>
      {!hideLabel && (
        <label htmlFor={fieldId}>
          {field.label}{" "}
          {requiredIndicator && field.required ? (
            <span className="req">*</span>
          ) : null}
        </label>
      )}
      <select
        aria-describedby={error ? errorId : undefined}
        aria-invalid={error ? "true" : "false"}
        className={error ? "has-error" : undefined}
        disabled={field.readOnly}
        id={fieldId}
        onChange={(event) => {
          const nextValue = event.target.value;
          if (!nextValue) {
            onChange(null);
            return;
          }
          onChange(nextValue);
        }}
        value={selectedValue}
      >
        <option value="">{field.placeholder ?? "Select..."}</option>
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            title={option.description}
          >
            {option.label}
          </option>
        ))}
      </select>
      {supportsOther && isOtherOptionValue(selectedValue) ? (
        <OtherTextInput
          describedBy={error ? errorId : undefined}
          disabled={field.readOnly}
          field={field}
          id={`${fieldId}-other`}
          onChange={(nextValue) => onChange(nextValue || null)}
          value={otherText}
        />
      ) : null}
      <FieldErrorMessage error={error} errorId={errorId} />
    </div>
  );
}

function MultiSelectField({
  className,
  error,
  field,
  idPrefix,
  onChange,
  requiredIndicator,
  value,
  hideLabel,
}: SharedFieldProps & { value: string[] }) {
  const fieldId = `${idPrefix}-${field.fieldKey}`;
  const errorId = `${fieldId}-error`;
  const supportsOther = fieldAllowsOtherValue(field);
  const options = supportsOther
    ? [...field.options, OTHER_OPTION]
    : field.options;
  const customOtherValue = supportsOther
    ? (value.find((item) => isCustomOtherValue(field, item)) ?? "")
    : "";
  const hasOtherSelection = supportsOther
    ? value.some(
        (item) => isOtherOptionValue(item) || isCustomOtherValue(field, item),
      )
    : false;

  function toggleValue(option: ProfileFieldOption) {
    const { isExclusive, value: optionValue } = option;

    if (isExclusive) {
      onChange(value.includes(optionValue) ? [] : [optionValue]);
      return;
    }

    const sanitizedValue = value.filter(
      (item) => !isCustomOtherValue(field, item),
    );

    if (supportsOther && isOtherOptionValue(optionValue)) {
      onChange(
        hasOtherSelection
          ? sanitizedValue.filter((item) => !isOtherOptionValue(item))
          : [
              ...sanitizedValue.filter(
                (item) =>
                  !field.options.some(
                    (candidate) =>
                      candidate.isExclusive && candidate.value === item,
                  ),
              ),
              optionValue,
            ],
      );
      return;
    }

    onChange(
      sanitizedValue.includes(optionValue)
        ? sanitizedValue.filter((item) => item !== optionValue)
        : [
            ...sanitizedValue.filter(
              (item) =>
                !field.options.some(
                  (candidate) =>
                    candidate.isExclusive && candidate.value === item,
                ),
            ),
            optionValue,
          ],
    );
  }

  function handleOtherChange(nextValue: string) {
    const baseValues = value.filter(
      (item) => !isOtherOptionValue(item) && !isCustomOtherValue(field, item),
    );
    const trimmedValue = nextValue.trim();

    if (!trimmedValue) {
      onChange(baseValues);
      return;
    }

    onChange([...baseValues, trimmedValue]);
  }

  return (
    <div className={fieldClassName(className)}>
      {!hideLabel && (
        <label htmlFor={fieldId}>
          {field.label}{" "}
          {requiredIndicator && field.required ? (
            <span className="req">*</span>
          ) : null}
        </label>
      )}
      <div
        aria-describedby={error ? errorId : undefined}
        aria-invalid={error ? "true" : "false"}
        className={`pill-group${error ? " has-error" : ""}`}
        id={fieldId}
      >
        {options.map((option) => {
          const selected = isOtherOptionValue(option.value)
            ? hasOtherSelection
            : value.includes(option.value);

          return (
            <button
              aria-describedby={
                option.description
                  ? `${fieldId}-${option.value}-description`
                  : undefined
              }
              aria-pressed={selected}
              className={`pill ${selected ? "on" : ""}`}
              disabled={field.readOnly}
              key={option.value}
              onClick={() => toggleValue(option)}
              type="button"
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {supportsOther && hasOtherSelection ? (
        <OtherTextInput
          describedBy={error ? errorId : undefined}
          disabled={field.readOnly}
          field={field}
          id={`${fieldId}-other`}
          onChange={handleOtherChange}
          value={customOtherValue}
        />
      ) : null}
      {field.options.map((option) =>
        option.description ? (
          <div
            className="field-note"
            id={`${fieldId}-${option.value}-description`}
            key={`${option.value}-description`}
          >
            {option.description}
          </div>
        ) : null,
      )}
      <FieldErrorMessage error={error} errorId={errorId} />
    </div>
  );
}

function RadioField({
  className,
  error,
  field,
  idPrefix,
  onChange,
  requiredIndicator,
  value,
  hideLabel,
}: SharedFieldProps & { value: string }) {
  const fieldId = `${idPrefix}-${field.fieldKey}`;
  const errorId = `${fieldId}-error`;
  const supportsOther = fieldAllowsOtherValue(field);
  const options = supportsOther
    ? [...field.options, OTHER_OPTION]
    : field.options;
  const otherText =
    supportsOther && isCustomOtherValue(field, value) ? value : "";
  const selectedValue = supportsOther && otherText ? "other" : value;

  return (
    <div className={fieldClassName(className)}>
      {!hideLabel && (
        <label>
          {field.label}{" "}
          {requiredIndicator && field.required ? (
            <span className="req">*</span>
          ) : null}
        </label>
      )}
      <div
        aria-describedby={error ? errorId : undefined}
        aria-invalid={error ? "true" : "false"}
        className="radio-group"
        id={fieldId}
      >
        {options.map((option) => {
          const optionId = `${fieldId}-${option.value}`;
          return (
            <label
              className={`pill radio-option${error ? " has-error" : ""}`}
              htmlFor={optionId}
              key={option.value}
            >
              <input
                checked={selectedValue === option.value}
                disabled={field.readOnly}
                id={optionId}
                name={fieldId}
                onChange={() => onChange(option.value)}
                type="radio"
              />
              <span>
                {option.label}
                {option.description ? (
                  <small>{option.description}</small>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>
      {supportsOther && isOtherOptionValue(selectedValue) ? (
        <OtherTextInput
          describedBy={error ? errorId : undefined}
          disabled={field.readOnly}
          field={field}
          id={`${fieldId}-other`}
          onChange={(nextValue) => onChange(nextValue || null)}
          value={otherText}
        />
      ) : null}
      <FieldErrorMessage error={error} errorId={errorId} />
    </div>
  );
}

function TextLikeField({
  className,
  error,
  field,
  idPrefix,
  onChange,
  requiredIndicator,
  value,
  hideLabel,
}: SharedFieldProps & { value: string }) {
  const fieldId = `${idPrefix}-${field.fieldKey}`;
  const errorId = `${fieldId}-error`;
  const inputType = htmlInputType(field.type);

  return (
    <div className={fieldClassName(className)}>
      {!hideLabel && (
        <label htmlFor={fieldId}>
          {field.label}{" "}
          {requiredIndicator && field.required ? (
            <span className="req">*</span>
          ) : null}
        </label>
      )}
      <input
        aria-describedby={error ? errorId : undefined}
        aria-invalid={error ? "true" : "false"}
        className={error ? "has-error" : undefined}
        id={fieldId}
        inputMode={inputModeFor(field.type)}
        onBlur={(event) =>
          onChange(normalizeTypedValue(field.type, event.target.value))
        }
        onChange={(event) => onChange(event.target.value)}
        placeholder={field.placeholder}
        readOnly={field.readOnly}
        step={field.type === "datetime" ? 60 : undefined}
        type={inputType}
        value={value}
      />
      <FieldErrorMessage error={error} errorId={errorId} />
    </div>
  );
}

function OtherTextInput({
  describedBy,
  disabled,
  field,
  id,
  onChange,
  value,
}: {
  describedBy?: string;
  disabled?: boolean;
  field: ProfileField;
  id: string;
  onChange: (value: string) => void;
  value: string;
}) {
  return (
    <input
      aria-describedby={describedBy}
      className="other-input"
      disabled={disabled}
      id={id}
      onBlur={(event) => onChange(event.target.value.trim())}
      onChange={(event) => onChange(event.target.value)}
      placeholder={field.placeholder ?? "Please specify"}
      type="text"
      value={value}
    />
  );
}

function htmlInputType(type: ProfileFieldType): HTMLInputTypeAttribute {
  switch (type) {
    case "email":
      return "email";
    case "number":
      return "number";
    case "date":
      return "date";
    case "datetime":
      return "datetime-local";
    case "mobile":
    case "landline":
      return "tel";
    default:
      return "text";
  }
}

function inputModeFor(
  type: ProfileFieldType,
): HTMLAttributes<HTMLInputElement>["inputMode"] {
  switch (type) {
    case "email":
      return "email";
    case "number":
      return "numeric";
    case "mobile":
    case "landline":
      return "tel";
    default:
      return undefined;
  }
}

function normalizeTypedValue(
  type: ProfileFieldType,
  value: string,
): string | null {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  switch (type) {
    case "email":
      return trimmedValue.toLowerCase();
    case "mobile":
    case "landline":
      return normalizePhone(trimmedValue);
    default:
      return trimmedValue;
  }
}

function normalizePhone(value: string): string {
  return value
    .replace(/[^\d+()\s-]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function fieldClassName(className?: string) {
  return className ? `field ${className}` : "field";
}

function FieldErrorMessage({
  error,
  errorId,
}: {
  error?: FieldError | string;
  errorId: string;
}) {
  const message = typeof error === "string" ? error : error?.message;
  return message ? (
    <div className="err-msg" id={errorId}>
      {message}
    </div>
  ) : null;
}
